<template>
  <div class="periods-comparison-container mt-12">
    <div class="header-section">
      <h1 class="mb-2">Comparativo entre períodos</h1>
      <p class="mb-6">
        Selecione um período de referência para analisar a evolução dos consumidores nos diferentes grupos e a migração de consumidores entre eles.
      </p>
    </div>
    <div class="period-section mb-6">
      <h2 class="mb-4">Período Referência</h2>
      <month-range-picker
        :key="referencePeriodMonthRangePickerKey"
        :min="minAnalyticPeriod"
        :max="maxAnalyticPeriod"
        :initial-date="referencePeriodInitialDate"
        :final-date="referencePeriodFinalDate"
        :is-dynamic-period="isDynamicPeriod"
        variant="reference"
        :disabled="isNotRecurrent"
        :is-not-recurrent="isNotRecurrent"
        @set-dates="setReferenceDatesLabels"
        @input="setReferenceDatesValues"
      />
    </div>
    <div class="graph-table-section">
      <indicators-evolution
        :loading-reference-period="loadingReferencePeriod"
        :loading-current-period="loadingCurrentPeriod"
        :formatted-current-dates="formattedCurrentDates"
        :formatted-reference-dates="formattedReferenceDates"
      />
      <migration-table :loading-reference-period="loadingReferencePeriod" :loading-current-period="loadingCurrentPeriod" />
    </div>
  </div>
</template>

<script>
import IndicatorsEvolution from './IndicatorsEvolution.vue'
import MigrationTable from './MigrationTable.vue'
import MonthRangePicker from '../MonthRangePicker.vue'
import { rfvAnalysis } from '../../../mixins/analise-rfv'
import { DateTime } from 'luxon'
import { mapState } from 'vuex'

export default {
  name: 'ComparisonBetweenPeriods',
  components: {
    IndicatorsEvolution,
    MonthRangePicker,
    MigrationTable,
  },
  mixins: [rfvAnalysis],
  props: {
    loadingCurrentPeriod: {
      type: Boolean,
      default: false,
    },
    formattedCurrentDates: {
      type: Object,
    },
  },
  data: () => ({
    loadingReferencePeriod: true,
    formattedReferenceDates: null,
    isFirstTimeLoadingData: true,
    referencePeriodMonthRangePickerKey: 0,
  }),
  computed: {
    ...mapState({ stateFilters: (state) => state.filters }),
    referencePeriodInitialDate() {
      if (!this.referencePeriod?.initialDate) return ''
      else if (this.isDynamicPeriod || this.isNotRecurrent) return this.referencePeriod.initialDate
      return DateTime.fromFormat(this.referencePeriod.initialDate, 'yyyy-MM-dd').toFormat('yyyy-MM')
    },
    referencePeriodFinalDate() {
      if (!this.referencePeriod?.finalDate) return ''
      else if (this.isDynamicPeriod || this.isNotRecurrent) return this.referencePeriod.finalDate
      return DateTime.fromFormat(this.referencePeriod.finalDate, 'yyyy-MM-dd').toFormat('yyyy-MM')
    },
    isDynamicPeriod() {
      return this.referencePeriod?.dynamic
    },
    skippingLoad() {
      return this.isQuickAction && this.isFirstTimeLoadingData
    },
    isNotRecurrent() {
      return this.analyticsConfiguration?.model === 'NOT_RECURRENT'
    },
  },
  async created() {
    if (this.$route.query.quick_action === 'true') {
      this.isQuickAction = true
    }
  },
  methods: {
    setReferenceDatesLabels(value) {
      this.$emit('set-reference-dates-labels', value)
      this.formattedReferenceDates = value
    },
    async setReferenceDatesValues(value) {
      const isCurrentMonth = value[1] === DateTime.fromFormat(this.maxAnalyticPeriod, 'yyyy-MM-dd').toFormat('yyyy-MM')
      const newReferencePeriod = isCurrentMonth
        ? this.analyticAllPeriods[0]
        : this.analyticAllPeriods.find(
            (period) =>
              DateTime.fromFormat(period.initialDate, 'yyyy-MM-dd').toFormat('yyyy-MM') === value[0] &&
              DateTime.fromFormat(period.finalDate, 'yyyy-MM-dd').toFormat('yyyy-MM') === value[1]
          )
      if (newReferencePeriod) {
        this.setReferencePeriod(newReferencePeriod)
        await this.loadReferenceData()
        this.$emit('load-migration-data')
      } else {
        this.$snackbar({ message: 'Não foi possível encontrar dados para o período selecionado.', snackbarColor: 'error' })
      }
    },
    async loadReferenceData() {
      this.loadingReferencePeriod = true
      const referenceVariables = {
        analyticConfigurationId: this.analyticsConfiguration._id,
        periodId: this.referencePeriod._id,
        segments: this.stateFilters.segments,
      }
      await this.getClientsMetrics({ variables: referenceVariables, periodType: 'reference' })
      this.loadingReferencePeriod = false
    },
  },
}
</script>

<style lang="scss" scoped>
.periods-comparison-container {
  .header-section {
    h1 {
      color: $cinza-d10;
      font-size: 22px;
      font-weight: 700;
      line-height: 27px;
    }
    p {
      font-size: 14px;
      color: $cinza-0;
      line-height: 20px;
    }
  }
  .period-section {
    h2 {
      color: $cinza-d10;
      font-size: 18px;
      font-weight: 700;
      letter-spacing: 0.333px;
    }
  }
  .graph-table-section {
    width: 100%;
    display: inline-flex;
    padding: 20px 24px;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-start;
    gap: 40px;
    border-radius: 8px;
    background: #fff;
    /* Card */
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.16);
  }
}
</style>
